import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Card, Divider, Modal, notification, Spin } from 'antd';
import {
    CloseOutlined,
    ExportOutlined,
    LoadingOutlined,
    PlusCircleOutlined,
    SaveOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { PartnerIdentity } from './ObtPartnerDetailsTab';
import { IObtContactModel } from '../../../__generated__/types';
import client from '../../../config/GraphQLApolloClient';
import { QUERY_REFRESH_PARTNER_CONTACT, QUERY_UPDATE_CONTACT } from '../obtQueries';
import { MUTATION_UPDATE_CONTACT_STATUS } from '../obtMutations';
import {
    buildContactColumns,
    exportContactsToExcel,
    getContactRoles,
    validateSingleSelection,
    VALID_EMAIL_REGEXP,
} from './obtContactUtils';

// ─── Types ──────────────────────────────────────────────────────────────────────

type ObtContactTabProps = {
    partnerIdentity: PartnerIdentity;
    partnerContact: IObtContactModel[];
    idCompany?: number;
    canUpdate: boolean;
};

// ─── Component ──────────────────────────────────────────────────────────────────

const ObtContactTab: React.FC<ObtContactTabProps> = ({
    partnerIdentity,
    partnerContact,
    idCompany,
    canUpdate,
}) => {
    const intl = useIntl();
    const gridApiRef = useRef<GridApi | null>(null);

    const [contactLoading, setContactLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [rowData, setRowData] = useState<IObtContactModel[]>(partnerContact ?? []);
    const [isDirty, setIsDirty] = useState(false);
    const [_selectedCount, setSelectedCount] = useState(0);

    const contactRoles = useMemo(
        () => getContactRoles(partnerIdentity.companycode, partnerIdentity.systemCode),
        [partnerIdentity.companycode, partnerIdentity.systemCode]
    );

    const columnDefs = useMemo(() => buildContactColumns(intl, contactRoles, canUpdate), [canUpdate, intl, contactRoles]);

    const defaultColDef = useMemo<ColDef>(
        () => ({ sortable: true, filter: true, resizable: true, editable: false }),
        []
    );

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const onCellValueChanged = useCallback((_event: CellValueChangedEvent) => {
        setIsDirty(true);
    }, []);

    const onSelectionChanged = useCallback((_event: SelectionChangedEvent) => {
        const count = gridApiRef.current?.getSelectedRows().length ?? 0;
        setSelectedCount(count);
    }, []);

    const refreshContacts = useCallback(async () => {
        setContactLoading(true);
        try {
            const response = await client.query({
                query: QUERY_REFRESH_PARTNER_CONTACT,
                variables: {
                    filters: {
                        partnerno: partnerIdentity.partnerno,
                        idpartner: partnerIdentity.idpartner,
                        companycode: [partnerIdentity.companycode],
                        idCompany: idCompany ?? undefined,
                        limit: 0, offset: 0, pageNo: 0, pageSize: 0,
                    },
                },
                fetchPolicy: 'no-cache',
            });
            setRowData(response.data?.obtRefreshPartnerContactDetails ?? []);
            setIsDirty(false);
        } catch (err) {
            console.error('Failed to refresh contacts:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.contacts.refresh.error', defaultMessage: 'Failed to refresh contacts.' }),
            });
        } finally {
            setContactLoading(false);
        }
    }, [partnerIdentity, idCompany, intl]);

    const onNewRow = useCallback(() => {
        gridApiRef.current?.applyTransaction({
            add: [{
                id: undefined, partnerno: partnerIdentity.partnerno, idpartner: partnerIdentity.idpartner,
                companycode: partnerIdentity.companycode, role: '', mail: '', source: 'I', valid: true,
                name1: '', name2: '', department: '', phone: '', fax: '', error: '',
            }],
            addIndex: 0,
        });
        setIsDirty(true);
    }, [partnerIdentity]);

    const showSaveConfirmModal = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;
        const selectedRows = api.getSelectedRows();
        if (selectedRows.length === 0) {
            notification.warning({
                message: intl.formatMessage({ id: 'obt.contacts.noRowSelected', defaultMessage: 'No row selected.' }),
                description: intl.formatMessage({ id: 'obt.contacts.noRowSelected.desc', defaultMessage: 'Please select a row to update and try again.' }),
            });
            return;
        }
        setShowSaveModal(true);
    }, [intl]);

    const handleSaveOk = useCallback(async () => {
        setShowSaveModal(false);
        const api = gridApiRef.current;
        if (!api) return;
        const selectedRows = api.getSelectedRows();
        if (selectedRows.length === 0) return;
        const rowToSave = { ...selectedRows[0], idpartner: partnerIdentity.idpartner };
        if (!rowToSave.mail || !VALID_EMAIL_REGEXP.test(rowToSave.mail)) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.contacts.save.emailError', defaultMessage: 'Invalid email address.' }),
                description: intl.formatMessage({ id: 'obt.contacts.save.emailError.desc', defaultMessage: 'Please provide a valid email address.' }),
            });
            return;
        }
        delete rowToSave.__typename; delete rowToSave.error; delete rowToSave.anlageUser;
        delete rowToSave.anlageDatum; delete rowToSave.aenderungUser; delete rowToSave.aenderungDatum;
        delete rowToSave._rowId; delete rowToSave.status;
        setContactLoading(true);
        try {
            await client.mutate({ mutation: QUERY_UPDATE_CONTACT, variables: { contactModel: rowToSave }, fetchPolicy: 'no-cache' });
            notification.success({ message: intl.formatMessage({ id: 'obt.contacts.save.success', defaultMessage: 'Contact updated successfully.' }) });
            setIsDirty(false);
            await refreshContacts();
        } catch (err) {
            console.error('Failed to save contact:', err);
            notification.error({ message: intl.formatMessage({ id: 'obt.contacts.save.error', defaultMessage: 'Failed to save contact.' }) });
        } finally {
            setContactLoading(false);
        }
    }, [partnerIdentity, intl, refreshContacts]);

    const showCancelContactModal = useCallback(() => {
        const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
        const selected = validateSingleSelection(selectedRows, intl,
            'obt.contacts.cancelModal.selectFirst', 'Please select a contact to cancel.',
            'obt.contacts.cancelModal.selectFirst', 'Please select a contact to cancel.',
            'obt.contacts.cancel.tooMany', 'Multiple contacts selected.',
            'obt.contacts.cancel.tooMany.desc', 'Please select only one contact at a time to cancel.',
        );
        if (!selected) return;
        setShowCancelModal(true);
    }, [intl]);

    const handleCancelOk = useCallback(async () => {
        const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
        if (selectedRows.length === 0) return;
        setContactLoading(true);
        try {
            const response = await client.mutate({ mutation: MUTATION_UPDATE_CONTACT_STATUS, variables: { idContact: selectedRows[0].id }, fetchPolicy: 'no-cache' });
            if (response.data?.obtUpdateContactStatus === 1) {
                notification.success({ message: intl.formatMessage({ id: 'obt.contacts.cancel.success', defaultMessage: 'Contact status updated successfully.' }) });
                await refreshContacts();
            }
            setShowCancelModal(false);
        } catch (err) {
            console.error('Failed to cancel contact:', err);
            notification.error({ message: intl.formatMessage({ id: 'obt.contacts.cancel.error', defaultMessage: 'Failed to cancel contact.' }) });
            setShowCancelModal(false);
        } finally {
            setContactLoading(false);
        }
    }, [intl, refreshContacts]);

    const onExportContacts = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;
        const exported = exportContactsToExcel(api, columnDefs, partnerIdentity.partnerno);
        if (!exported) {
            notification.warning({
                message: intl.formatMessage({ id: 'obt.contacts.export.noData', defaultMessage: 'No contacts to export.' }),
            });
        } else {
            notification.success({
                message: intl.formatMessage({ id: 'obt.contacts.export.success', defaultMessage: 'Contacts exported successfully.' }),
            });
        }
    }, [columnDefs, intl, partnerIdentity.partnerno]);

    return (
        <Spin spinning={contactLoading} size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} />}>
            <Card size="small" title={intl.formatMessage({ id: 'obt.contacts.card.title', defaultMessage: 'Contacts' })}>
                <div style={{ marginBottom: 12 }}>
                    <Button icon={<PlusCircleOutlined />} type="primary" onClick={onNewRow} disabled={!canUpdate}>
                        {intl.formatMessage({ id: 'obt.contacts.btn.new', defaultMessage: 'New' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<SaveOutlined />} type="primary" onClick={showSaveConfirmModal} disabled={!canUpdate || !isDirty}>
                        {intl.formatMessage({ id: 'obt.contacts.btn.save', defaultMessage: 'Save' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<SyncOutlined />} type="primary" onClick={refreshContacts}>
                        {intl.formatMessage({ id: 'obt.contacts.btn.refresh', defaultMessage: 'Refresh' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<CloseOutlined />} type="primary" danger={true} onClick={showCancelContactModal} disabled={!canUpdate}>
                        {intl.formatMessage({ id: 'obt.contacts.btn.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Divider type="vertical" />
                    <Divider type="vertical" />
                    <Button icon={<ExportOutlined />} onClick={onExportContacts}>
                        {intl.formatMessage({ id: 'obt.contacts.btn.export', defaultMessage: 'Export' })}
                    </Button>
                </div>

                <Modal
                    key="save-contact"
                    title={intl.formatMessage({ id: 'obt.contacts.saveModal.title', defaultMessage: 'Save Contact' })}
                    open={showSaveModal} onOk={handleSaveOk} onCancel={() => setShowSaveModal(false)}
                    okText={intl.formatMessage({ id: 'obt.popconfirm.yes', defaultMessage: 'Yes' })}
                    cancelText={intl.formatMessage({ id: 'obt.popconfirm.no', defaultMessage: 'No' })}
                >
                    <p>{intl.formatMessage({ id: 'obt.contacts.save.confirm', defaultMessage: 'Save contact changes?' })}</p>
                </Modal>

                <Modal
                    key="cancel-contact"
                    title={intl.formatMessage({ id: 'obt.contacts.cancelModal.title', defaultMessage: 'Cancel Contact(s)' })}
                    open={showCancelModal} onOk={handleCancelOk} onCancel={() => setShowCancelModal(false)}
                    okText={intl.formatMessage({ id: 'obt.popconfirm.yes', defaultMessage: 'Yes' })}
                    cancelText={intl.formatMessage({ id: 'obt.popconfirm.no', defaultMessage: 'No' })}
                >
                    <p>{intl.formatMessage({ id: 'obt.contacts.cancelModal.prompt', defaultMessage: 'Cancel selected contacts?' })}</p>
                </Modal>

                <div className="ag-theme-balham" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                        rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
                        onGridReady={onGridReady} onCellValueChanged={onCellValueChanged}
                        onSelectionChanged={onSelectionChanged}
                        rowSelection="multiple"
                        pagination={true} paginationAutoPageSize={true} animateRows={true}
                        stopEditingWhenCellsLoseFocus={true} singleClickEdit={true}
                    />
                </div>
            </Card>
        </Spin>
    );
};

export default ObtContactTab;
